import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLocale } from '../../../contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { InboxNavItem } from './InboxNavItem';
import { useInboxContext } from '@/presentation/contexts/InboxContext';

interface HomeTopBarHeaderProps {
  isExpanded: boolean;
  onExpandedChange: (value: boolean) => void;
}

export const HomeTopBarHeader = ({
  isExpanded,
  onExpandedChange,
}: HomeTopBarHeaderProps) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const location = useLocation();
  const measureRef = useRef<HTMLDivElement>(null);
  const [itemWidths, setItemWidths] = useState<{ [key: string]: number }>({});
  // Inbox unseen-badge truth, read DIRECTLY off the shared inbox core via
  // context (the web analogue of Flutter's single shared NewChatMessageNotifier).
  // `InboxProvider` is mounted in `ProtectedRoutesWrapper` ABOVE the
  // `KeepAliveOutlet` route cache, so BOTH cached `HomeTopBarHeader` instances
  // (Feed + Inbox) read the SAME live value and re-render together when it flips.
  // This replaces the former per-instance `useInboxUnseenBadge` event-bus
  // subscription, whose local `useState` never replayed the last emit to a
  // late-mounting cached subtree — the Feed↔Inbox on/off loop (finding 5). The
  // single shared core fetches the first inbox pages once at startup and keeps
  // ONE chat subscription live for the session, so the dot appears passively AND
  // updates live for incoming chats even while the Inbox is closed (finding 1).
  const { hasUnseenInbox } = useInboxContext();

  const navItems = useMemo(() => [
    { label: l10n.topBar.feed, path: '/', qaId: 'topbar-tab-feed' },
    { label: l10n.routes.inbox, path: '/inbox', qaId: 'topbar-tab-inbox' },
  ], [l10n]);

  const isActive = (path: string) => {
    if (path === '/' && (location.pathname === '/' || location.pathname === '/feed')) {
      return true;
    }
    return location.pathname === path;
  };

  // Measure each nav item's bold width via canvas to prevent layout shift on active toggle
  useEffect(() => {
    if (!measureRef.current) return;

    const widths: { [key: string]: number } = {};
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (context) {
      const fontSize = responsive.font.lg;
      const fontWeight = responsive.fontWeight.semibold;
      context.font = `${fontWeight} ${fontSize} system-ui, -apple-system, sans-serif`;

      navItems.forEach((item) => {
        const metrics = context.measureText(item.label);
        const letterSpacingExtra = item.label.length * responsive.topBar.activeLetterSpacing; // Match CSS letter spacing for active item
        widths[item.path] = Math.ceil(metrics.width + letterSpacingExtra + 2);
      });

      setItemWidths(widths);
    }
  }, [navItems, responsive.font.lg, responsive.fontWeight.semibold]);

  return (
    <div
      className="flex items-center"
      style={{
        height: responsive.topBar.height,
        marginTop: responsive.safeArea.top,
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg
      }}
    >
      {/* Hidden font measurement anchor */}
      <div ref={measureRef} style={{ display: 'none' }} />

      {/* Left: Search Button */}
      <div className="flex-1 flex justify-start">
        <button
          {...qaAttr({ id: 'topbar-search' })}
          onClick={() => Navigator.navigateToSearch()}
          className="flex items-center justify-center hover:opacity-80"
          style={{
            borderRadius: responsive.borderRadius.round,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            opacity: isExpanded ? 0 : 1,
            transition: `opacity ${ANIMATION_CONSTANTS.duration.normal}ms ${ANIMATION_CONSTANTS.easing.ease}`,
            pointerEvents: isExpanded ? 'none' : 'auto',
          }}
        >
          <AssetIcon path="/assets/vectors/search.svg" />
        </button>
      </div>

      {/* Center: Nav Links - fade out when expanded */}
      <div
        className="flex items-center"
        style={{
          gap: responsive.gap['2xl'],
          opacity: isExpanded ? 0 : 1,
          transition: `opacity ${ANIMATION_CONSTANTS.duration.normal}ms ${ANIMATION_CONSTANTS.easing.ease}`,
          pointerEvents: isExpanded ? 'none' : 'auto',
        }}
      >
        {navItems.map((item) => {
          const active = isActive(item.path);
          const width = itemWidths[item.path];
          const isInbox = item.path === '/inbox';

          if (isInbox) {
            return (
              <InboxNavItem
                key={item.path}
                label={item.label}
                path={item.path}
                qaId={item.qaId}
                active={active}
                width={width}
                showUnseenDot={hasUnseenInbox}
              />
            );
          }

          return (
            <Link
              key={item.path}
              {...qaAttr({ id: item.qaId, value: active ? 'active' : undefined })}
              to={item.path}
              className="hover:opacity-80 inline-block text-center"
              style={{
                transition: `all ${ANIMATION_CONSTANTS.duration.fast}ms ${ANIMATION_CONSTANTS.easing.easeInOut}`,
                color: active ? theme.text.primary : theme.text.secondary,
                // If changes done here for font size, font weight or letter spacing,
                // make sure to update the canvas measurement in useEffect above accordingly
                fontSize: responsive.font.lg,
                fontWeight: active ? responsive.fontWeight.semibold : responsive.fontWeight.regular,
                letterSpacing: active ? `${responsive.topBar.activeLetterSpacing}px` : '0px',
                width: width ? `${width}px` : 'auto',
                minWidth: width ? `${width}px` : 'auto',
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </div>

      {/* Right: Expand/Collapse Button */}
      <div
        className="flex-1 flex justify-end items-center"
        style={{ gap: responsive.gap.lg }}
      >
        <button
          {...qaAttr({ id: 'topbar-expand', status: isExpanded ? 'expanded' : 'collapsed' })}
          onClick={() => onExpandedChange(!isExpanded)}
          className="flex items-center justify-center hover:opacity-80"
          style={{
            borderRadius: responsive.borderRadius.round,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <AssetIcon
            path="/assets/vectors/expand.svg"
            rotationAngle={!isExpanded ? ANIMATION_CONSTANTS.rotation.down : ANIMATION_CONSTANTS.rotation.up}
            transition={`transform ${ANIMATION_CONSTANTS.duration.slow}ms ${ANIMATION_CONSTANTS.easing.ease}`}
          />
        </button>
      </div>
    </div>
  );
};
